"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { supabase } from "@/utils/supabase/client";
import useStore from "@/store/useStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSpinner,
  faExternalLinkAlt,
  faTimes,
  faBookmark,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { faBookmark as faBookmarkOutline } from "@fortawesome/free-regular-svg-icons";
import { useToast } from "@/hooks/use-toast";
import DiscoveryMode from "@/components/qonnect/DiscoveryMode";
import {
  useDebounce,
  AnimatedGrid,
  AnimatedGridItem,
  SearchWrapper,
} from "@/components/ui/animated-grid";

export default function QonnectPage() {
  const { session } = useStore();
  const { toast } = useToast();
  const router = useRouter();
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDiscoveryMode, setShowDiscoveryMode] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const ITEMS_PER_PAGE = 15;

  const isAuthenticated = !!session?.user;
  const hasBetaAccess = session?.user?.user_settings?.beta_program || false;

  // Show searching state when search query changes
  useEffect(() => {
    if (searchQuery !== debouncedSearchQuery) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
  }, [searchQuery, debouncedSearchQuery]);

  // Category definitions
  const CATEGORIES = {
    all: { name: "All", description: "All spaces" },
    creators: {
      name: "Content Creators",
      description: "Creators & Influencers",
    },
    designers: { name: "Designers", description: "UI/UX & Visual Design" },
    developers: {
      name: "Developers",
      description: "Software & Web Development",
    },
    artists: { name: "Artists", description: "Art & Creative Works" },
    business: {
      name: "Business",
      description: "Companies & Professional Services",
    },
    portfolio: { name: "Portfolios", description: "Professional Showcases" },
    personal: { name: "Personal", description: "Personal Blogs & Profiles" },
    music: { name: "Music", description: "Musicians & Audio Artists" },
    photography: {
      name: "Photography",
      description: "Photographers & Visual Artists",
    },
    food: { name: "Food & Restaurants", description: "Culinary & Dining" },
    travel: { name: "Travel", description: "Travel & Adventure" },
    education: { name: "Education", description: "Learning & Teaching" },
    nonprofit: { name: "Non-Profit", description: "Charities & Causes" },
    other: { name: "Other", description: "Everything Else" },
  };

  // Fetch spaces function with pagination
  const fetchSpaces = useCallback(
    async (pageNum = 1) => {
      try {
        setLoading(true);

        const { data, error } = await supabase.rpc("get_qonnect_spaces", {
          p_user_id: session?.user?.id || null,
          p_category: selectedCategory,
          p_search_query: debouncedSearchQuery || null,
          p_limit: ITEMS_PER_PAGE,
          p_offset: (pageNum - 1) * ITEMS_PER_PAGE,
        });

        if (error) throw error;

        const newSpaces = data || [];
        setSpaces(newSpaces);

        // Calculate total pages based on whether we got a full page
        // If we got less than ITEMS_PER_PAGE, this is the last page
        if (newSpaces.length < ITEMS_PER_PAGE) {
          setTotalPages(pageNum);
        } else {
          // If we got a full page, there might be more pages
          // We'll assume there's at least one more page
          setTotalPages(pageNum + 1);
        }
      } catch (error) {
        console.error("Error fetching spaces:", error);
        toast({
          title: "Error",
          description: "Failed to load spaces. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [session?.user?.id, selectedCategory, debouncedSearchQuery, toast]
  );

  // Initial load and when filters change - reset to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, debouncedSearchQuery]);

  // Fetch spaces when page or filters change
  useEffect(() => {
    fetchSpaces(currentPage);
  }, [currentPage, selectedCategory, debouncedSearchQuery, fetchSpaces]);

  // Handle favorite toggle
  const handleFavorite = useCallback(
    async (spaceId) => {
      if (!session?.user?.id) {
        router.push("/signin");
        return;
      }

      try {
        const { data, error } = await supabase.rpc("toggle_space_favorite", {
          p_user_id: session.user.id,
          p_space_id: spaceId,
        });

        if (error) throw error;

        // Update the local state
        setSpaces((prev) =>
          prev.map((space) =>
            space.id === spaceId ? { ...space, is_favorited: data } : space
          )
        );

        toast({
          title: data ? "Added to favorites" : "Removed from favorites",
          description: data
            ? "Space has been added to your favorites"
            : "Space has been removed from your favorites",
          variant: "success",
        });
      } catch (error) {
        console.error("Error toggling favorite:", error);
        toast({
          title: "Error",
          description: "Failed to update favorite. Please try again.",
          variant: "destructive",
        });
      }
    },
    [session?.user?.id, router, toast]
  );

  const getCategoryColor = (category) => {
    const colors = {
      creators: "bg-purple-500/20 text-purple-300 border-purple-500/30",
      designers: "bg-pink-500/20 text-pink-300 border-pink-500/30",
      developers: "bg-blue-500/20 text-blue-300 border-blue-500/30",
      artists: "bg-orange-500/20 text-orange-300 border-orange-500/30",
      business: "bg-green-500/20 text-green-300 border-green-500/30",
      portfolio: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
      personal: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
      music: "bg-red-500/20 text-red-300 border-red-500/30",
      photography: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
      food: "bg-amber-500/20 text-amber-300 border-amber-500/30",
      travel: "bg-teal-500/20 text-teal-300 border-teal-500/30",
      education: "bg-violet-500/20 text-violet-300 border-violet-500/30",
      nonprofit: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
      other: "bg-gray-500/20 text-gray-300 border-gray-500/30",
    };
    return colors[category] || colors.other;
  };

  const getThemeColor = (theme) => {
    if (theme?.colors?.primary) {
      return theme.colors.primary;
    }
    return "#2563eb";
  };

  // Generate a profile image placeholder based on title
  const getProfileImagePlaceholder = (title) => {
    const colors = [
      "bg-purple-500",
      "bg-pink-500",
      "bg-blue-500",
      "bg-green-500",
      "bg-yellow-500",
      "bg-red-500",
      "bg-indigo-500",
      "bg-orange-500",
    ];
    const colorIndex = title?.charCodeAt(0) % colors.length || 0;
    return colors[colorIndex];
  };

  // Parse bio image URL - handle both string and JSON object formats
  const parseBioImageUrl = (bioImageUrl) => {
    if (!bioImageUrl || bioImageUrl === "null" || bioImageUrl.trim() === "") {
      return null;
    }

    // If it looks like a JSON string, try to parse it
    if (bioImageUrl.startsWith("{") && bioImageUrl.includes("url")) {
      try {
        const parsed = JSON.parse(bioImageUrl);
        return parsed.url || null;
      } catch (error) {
        console.warn("Failed to parse bio image JSON:", bioImageUrl, error);
        return null;
      }
    }

    // Otherwise, treat it as a direct URL string
    return bioImageUrl;
  };

  const hasResults = spaces.length > 0 || loading;

  return (
    <div className="min-h-screen bg-dark-deeper">
      {/* Header */}
      <header className="border-b border-dark-elevated bg-dark-deeper">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-2">
              <Image
                src="/logos/beta/white.svg"
                alt="Qensa Logo"
                width={100}
                height={100}
              />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link href="/qonnect" className="text-accent-lime font-medium">
                QONNECT
              </Link>
              <Link
                href="https://qensa.com/whats-new"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-light/70 hover:text-neutral-light transition-colors"
              >
                What's New
              </Link>
              <Link
                href="/faq"
                className="text-neutral-light/70 hover:text-neutral-light transition-colors"
              >
                FAQ
              </Link>
              <Link
                href="/beta-program"
                className="text-neutral-light/70 hover:text-accent-lime transition-colors"
              >
                BETA
              </Link>
            </nav>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <Link href="/user/spaces">
                  <div className="w-12 h-12 rounded-full bg-neutral-light/20 hover:bg-neutral-light/30 flex items-center justify-center transition-colors">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-6 h-6 text-neutral-light"
                    >
                      <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
                      <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
                    </svg>
                  </div>
                </Link>
              ) : (
                <Link
                  href="/onboarding"
                  className="border border-neutral-light text-neutral-light px-4 py-2 rounded-lg font-medium hover:bg-neutral-light hover:text-dark-deeper transition-colors"
                >
                  Create your Qensa
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-dark-deeper py-16">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-neutral-light mb-4">
            Qensa Qonnect
          </h1>
          <p className="text-xl text-neutral-light/70 mb-12">
            Explore pages, find inspiration, and connect with people
          </p>

          {/* Discovery Mode Button - Only show for beta program users */}
          {/* {isAuthenticated && hasBetaAccess && (
            <div className="mb-8">
              <button
                onClick={() => setShowDiscoveryMode(true)}
                className="group relative px-8 py-4 bg-gradient-to-r from-accent-lime to-accent-teal rounded-xl font-semibold text-dark-deeper text-lg hover:shadow-2xl hover:shadow-accent-lime/20 transform hover:scale-105 transition-all duration-300"
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">✨</div>
                  <span>Discovery Mode</span>
                  <div className="text-xs bg-dark-deeper/20 px-2 py-1 rounded-full ml-2">
                    BETA
                  </div>
                </div>
                <div className="absolute -inset-0.5 bg-gradient-to-r from-accent-lime to-accent-teal rounded-xl opacity-0 group-hover:opacity-75 blur transition duration-300 -z-10"></div>
              </button>
              <p className="text-neutral-light/60 text-sm mt-2">
                Discover amazing spaces in a fun, swipe-to-explore way • Beta
                Feature
              </p>
            </div>
          )} */}

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-4">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-light/50 w-5 h-5"
              />
              <input
                type="text"
                placeholder="Search for people on Qensa"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-12 py-4 text-lg bg-dark-surface border border-dark-elevated rounded-xl text-neutral-light placeholder:text-neutral-light/50 focus:border-accent-lime focus:outline-none focus:ring-2 focus:ring-accent-lime/20 transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-neutral-light/50 hover:text-neutral-light transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Category Filters - Hidden for now */}
          {false && (
            <div className="flex flex-wrap justify-center gap-3">
              {Object.entries(CATEGORIES).map(([key, category]) => (
                <button
                  key={key}
                  onClick={() =>
                    setSelectedCategory(key === "all" ? null : key)
                  }
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === key ||
                    (!selectedCategory && key === "all")
                      ? "bg-green-400 text-black"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="bg-dark-deeper py-6">
        <div className="max-w-7xl mx-auto px-6">
          <SearchWrapper
            isSearching={isSearching}
            hasResults={hasResults && !loading}
            searchQuery={debouncedSearchQuery}
            emptyMessage="No spaces found"
          >
            {loading && !isSearching ? (
              <div className="flex items-center justify-center py-20">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="w-8 h-8 text-accent-lime animate-spin"
                />
              </div>
            ) : (
              <>
                {/* Spaces Grid */}
                <AnimatedGrid className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  {spaces.map((space, index) => (
                    <AnimatedGridItem
                      key={space.id}
                      layoutId={`space-${space.id}`}
                      delay={index}
                      className={`group relative bg-dark-surface rounded-3xl overflow-visible cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-accent-lime/20 ${
                        space.is_featured
                          ? "ring-2 ring-accent-lime"
                          : "hover:ring-1 hover:ring-accent-lime/30"
                      }`}
                      onClick={() => window.open(`/${space.slug}`, "_blank")}
                    >
                      {/* Featured Badge */}
                      {space.is_featured && (
                        <div className="absolute -top-3 right-[15px] z-10">
                          <div className="bg-accent-lime text-dark-deeper px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                            FEATURED
                          </div>
                        </div>
                      )}

                      {/* Card Header */}
                      <div className="px-6 pt-6 pb-3 text-center">
                        {/* Profile Image */}
                        <div className="w-16 h-16 rounded-full mx-auto mb-3 overflow-hidden relative">
                          {(() => {
                            const imageUrl = parseBioImageUrl(
                              space.bio_image_url
                            );
                            return imageUrl ? (
                              <>
                                <img
                                  src={imageUrl}
                                  alt={`${space.title} profile`}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                  onError={(e) => {
                                    // Fallback to letter placeholder if image fails to load
                                    e.target.style.display = "none";
                                    const placeholder =
                                      e.target.parentElement.querySelector(
                                        ".letter-placeholder"
                                      );
                                    if (placeholder) {
                                      placeholder.style.display = "flex";
                                    }
                                  }}
                                />
                                <div
                                  className={`letter-placeholder w-full h-full flex items-center justify-center text-neutral-light font-bold text-xl absolute inset-0 ${getProfileImagePlaceholder(space.title)}`}
                                  style={{ display: "none" }}
                                >
                                  {space.title?.charAt(0)?.toUpperCase() || "S"}
                                </div>
                              </>
                            ) : (
                              <div
                                className={`w-full h-full flex items-center justify-center text-neutral-light font-bold text-xl ${getProfileImagePlaceholder(space.title)}`}
                              >
                                {space.title?.charAt(0)?.toUpperCase() || "S"}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Title */}
                        <h3 className="text-neutral-light font-semibold text-lg mb-1">
                          {space.title}
                        </h3>

                        {/* Slug */}
                        <p className="text-accent-teal text-sm">
                          @{space.slug}
                        </p>

                        {/* Favorite Button - Only show if authenticated */}
                        {isAuthenticated && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFavorite(space.id);
                            }}
                            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-accent-teal/20 backdrop-blur-sm hover:bg-accent-teal/30 hover:scale-110 transition-all duration-200 flex items-center justify-center"
                          >
                            <FontAwesomeIcon
                              icon={
                                space.is_favorited
                                  ? faBookmark
                                  : faBookmarkOutline
                              }
                              className="w-5 h-5 text-accent-teal"
                            />
                          </button>
                        )}
                      </div>

                      {/* Space Preview Image */}
                      <div className="p-4">
                        <div className="aspect-[16/10] relative overflow-hidden rounded-3xl">
                          {space.preview_image_url ? (
                            <Image
                              src={space.preview_image_url}
                              alt={`${space.title} preview`}
                              fill
                              className="object-cover"
                              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                              loading="lazy"
                            />
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              style={{
                                background: `linear-gradient(135deg, ${getThemeColor(space.theme)}20, ${getThemeColor(space.theme)}40)`,
                              }}
                            >
                              <div className="text-center">
                                <div className="text-4xl font-bold text-white/30 mb-2">
                                  {space.title?.charAt(0)?.toUpperCase() || "S"}
                                </div>
                                <div className="text-sm text-white/50">
                                  {space.title}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* External Link Indicator */}
                          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-dark-deeper/80 backdrop-blur-sm flex items-center justify-center text-neutral-light/80 opacity-0 group-hover:opacity-100 transition-opacity">
                            <FontAwesomeIcon
                              icon={faExternalLinkAlt}
                              className="w-4 h-4"
                            />
                          </div>
                        </div>
                      </div>
                    </AnimatedGridItem>
                  ))}
                </AnimatedGrid>

                {/* Pagination */}
                {spaces.length > 0 && (
                  <div className="flex items-center justify-center gap-4 mt-12">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1 || loading}
                      className="px-4 py-2 rounded-xl bg-dark-surface border border-dark-elevated hover:bg-dark-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    >
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className="w-4 h-4 text-neutral-light"
                      />
                    </button>

                    <div className="flex items-center gap-2">
                      {(() => {
                        const pages = [];
                        const maxVisible = 7;

                        if (totalPages <= maxVisible) {
                          // Show all pages
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // Show first page
                          pages.push(1);

                          if (currentPage <= 4) {
                            // Show first 5 pages, ellipsis, last page
                            for (let i = 2; i <= 5; i++) {
                              pages.push(i);
                            }
                            pages.push("ellipsis");
                            pages.push(totalPages);
                          } else if (currentPage >= totalPages - 3) {
                            // Show first page, ellipsis, last 5 pages
                            pages.push("ellipsis");
                            for (let i = totalPages - 4; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            // Show first page, ellipsis, current-1, current, current+1, ellipsis, last page
                            pages.push("ellipsis");
                            pages.push(currentPage - 1);
                            pages.push(currentPage);
                            pages.push(currentPage + 1);
                            pages.push("ellipsis");
                            pages.push(totalPages);
                          }
                        }

                        return pages.map((pageNum, index) => {
                          if (pageNum === "ellipsis") {
                            return (
                              <span
                                key={`ellipsis-${index}`}
                                className="text-neutral-light/50 px-2"
                              >
                                ...
                              </span>
                            );
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              disabled={loading}
                              className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                                currentPage === pageNum
                                  ? "bg-accent-lime text-dark-deeper"
                                  : "bg-dark-surface border border-dark-elevated text-neutral-light hover:bg-dark-elevated"
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {pageNum}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages || loading}
                      className="px-4 py-2 rounded-xl bg-dark-surface border border-dark-elevated hover:bg-dark-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    >
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="w-4 h-4 text-neutral-light"
                      />
                    </button>
                  </div>
                )}
              </>
            )}
          </SearchWrapper>
        </div>
      </div>

      {/* Discovery Mode Modal */}
      <DiscoveryMode
        isOpen={showDiscoveryMode}
        onClose={() => setShowDiscoveryMode(false)}
        session={session}
      />
    </div>
  );
}
